import { DevTool } from '@hookform/devtools';
import type { Ref } from 'react';
import { type FieldErrors, type FieldValues, FormProvider, type FormProviderProps } from 'react-hook-form';
import { Box } from 'ui';

type Props<T extends FieldValues> = FormProviderProps<T> & {
  onSubmit?: (data: T) => void;
  'data-testid'?: string;
  onError?: (errors: FieldErrors<T>) => void;
  devtool?: boolean;
  formRef?: Ref<HTMLElement>;
  density?: 'dense' | 'normal';
};

export const Form = <T extends FieldValues>({
  children,
  onSubmit,
  onError,
  devtool = true,
  'data-testid': dataTestid,
  formRef,
  density = 'normal',
  ...rest
}: Props<T>) => {
  return (
    <FormProvider {...rest}>
      <Box
        ref={formRef}
        component="form"
        sx={{ display: 'flex', flexDirection: 'column', gap: density === 'dense' ? 1 : 2 }}
        autoComplete="off"
        onSubmit={
          onSubmit
            ? (e) => {
                rest.clearErrors();
                rest.handleSubmit(onSubmit, onError)(e);
              }
            : undefined
        }
        noValidate
        data-testid={dataTestid}
      >
        {children}
        {devtool && !import.meta.env.PROD ? <DevTool control={rest.control} placement="top-right" /> : null}
      </Box>
    </FormProvider>
  );
};
